// src/config/stages.js
// Single source of truth for opportunity stage vocabulary on the frontend.
// Mirrors api/shared/stage.js. Any frontend component that needs stage logic
// imports from this module. No component should define its own STAGES,
// ACTIVE_STAGES, CLOSED_STAGES, or STAGE_CONFIG locally.
//
// As of Deployment 1: this file exists but is not yet imported anywhere.
// Deployments C/D/E migrate OpportunitiesTab.jsx, PipelinePage.jsx, and
// CommandCenter.jsx to consume from this module.

// Probability labels (UI form) in funnel order
export const STAGES = [
  'Under Review',
  '10% - Prospecting',
  '25% - Qualification',
  '40% - Proposal Creation',
  '50% - Proposal Presented to Client',
  '60% - Orals',
  '70% - Q&A',
  '80% - BAFO',
  '90% - Contract Negotiation',
  '100% - Deal Signed by Client',
  '0% - Deal Lost / Tabled',
  'Reviewed / Not Advancing',
];

// Stages where work is ongoing (used by pipeline filters)
export const ACTIVE_STAGES = [
  'Under Review',
  '10% - Prospecting',
  '25% - Qualification',
  '40% - Proposal Creation',
  '50% - Proposal Presented to Client',
  '60% - Orals',
  '70% - Q&A',
  '80% - BAFO',
  '90% - Contract Negotiation',
];

// Terminal stages
export const CLOSED_STAGES = [
  '100% - Deal Signed by Client',
  '0% - Deal Lost / Tabled',
  'Reviewed / Not Advancing',
];

// Visual config keyed by probability label.
// color/bg: surface tokens. icon: short emoji. label: short display name. pct: numeric weight or null.
export const STAGE_CONFIG = {
  'Under Review':                       { color: '#6B7280', bg: '#F3F4F6', icon: '🔎', label: 'Under Review',  pct: null },
  '10% - Prospecting':                  { color: '#9CA3AF', bg: '#F3F4F6', icon: '🔍', label: 'Prospecting',    pct: 10  },
  '25% - Qualification':                { color: '#3B82F6', bg: '#DBEAFE', icon: '📋', label: 'Qualification',  pct: 25  },
  '40% - Proposal Creation':            { color: '#F59E0B', bg: '#FEF3C7', icon: '📝', label: 'Proposal',       pct: 40  },
  '50% - Proposal Presented to Client': { color: '#F97316', bg: '#FFF7ED', icon: '📤', label: 'Presented',      pct: 50  },
  '60% - Orals':                        { color: '#8B5CF6', bg: '#EDE9FE', icon: '🎤', label: 'Orals',          pct: 60  },
  '70% - Q&A':                          { color: '#7C3AED', bg: '#EDE9FE', icon: '❓', label: 'Q&A',            pct: 70  },
  '80% - BAFO':                         { color: '#6366F1', bg: '#E0E7FF', icon: '🎯', label: 'BAFO',           pct: 80  },
  '90% - Contract Negotiation':         { color: '#4F46E5', bg: '#E0E7FF', icon: '⚖️', label: 'Negotiation',    pct: 90  },
  '100% - Deal Signed by Client':       { color: '#10B981', bg: '#D1FAE5', icon: '✅', label: 'Signed',         pct: 100 },
  '0% - Deal Lost / Tabled':            { color: '#EF4444', bg: '#FEE2E2', icon: '❌', label: 'Lost / Tabled',  pct: 0   },
  'Reviewed / Not Advancing':           { color: '#92400E', bg: '#FEF3C7', icon: '📁', label: 'Not Advancing',  pct: null },
};

// Canonical short names (matches backend api/shared/stage.js CANONICAL_STAGES)
export const CANONICAL_STAGES = [
  'Under Review',
  'Prospecting',
  'Qualification',
  'Proposal Creation',
  'Proposal Presented',
  'Orals',
  'Q&A',
  'BAFO',
  'Contract Negotiation',
  'Closed Won',
  'Closed Lost',
  'Tabled',
];

// probability (UI label) -> canonical short name
// Includes legacy values still present in historical data.
export const PROBABILITY_TO_STAGE = {
  'Under Review':                          'Under Review',
  '10% - Prospecting':                     'Prospecting',
  '25% - Qualification':                   'Qualification',
  '40% - Proposal Creation':               'Proposal Creation',
  '50% - Proposal Presented to Client':    'Proposal Presented',
  '60% - Orals':                           'Orals',
  '70% - Q&A':                             'Q&A',
  '80% - BAFO':                            'BAFO',
  '90% - Contract Negotiation':            'Contract Negotiation',
  '100% - Deal Signed by Client':          'Closed Won',
  '0% - Deal Lost / Tabled':               'Closed Lost',
  'Reviewed / Not Advancing':              'Tabled',
  '60% - Contract Creation':               'Contract Negotiation',
  '70% - Contract Negotiation / Legal':    'Contract Negotiation',
};

// canonical short name -> probability label. Mirrors STAGE_TO_PROBABILITY in
// api/shared/stage.js.
//
// B92: the personas emit CANONICAL names ("Qualification"), because asking a model
// to spell "25% - Qualification" invites a near-miss that maps to nothing. The
// database column and the UI dropdown both hold the PROBABILITY label. Something
// has to translate, and this is it — used by TheFloor before it POSTs.
export const STAGE_TO_PROBABILITY = {
  'Under Review':         'Under Review',
  'Prospecting':          '10% - Prospecting',
  'Qualification':        '25% - Qualification',
  'Proposal Creation':    '40% - Proposal Creation',
  'Proposal Presented':   '50% - Proposal Presented to Client',
  'Orals':                '60% - Orals',
  'Q&A':                  '70% - Q&A',
  'BAFO':                 '80% - BAFO',
  'Contract Negotiation': '90% - Contract Negotiation',
  'Closed Won':           '100% - Deal Signed by Client',
  'Closed Lost':          '0% - Deal Lost / Tabled',
  'Tabled':               'Reviewed / Not Advancing',
};

export function normalizeStage(probability) {
  if (!probability) return null;
  return PROBABILITY_TO_STAGE[probability] || null;
}

/**
 * Turn whatever a persona emitted into a probability label the write path accepts.
 *
 * B92. TheFloor parsed `stage="Qualification"` out of an ACTION tag and sent it
 * as `probability`, which opportunities/create.js stores verbatim and feeds to
 * normalizeStage() — whose keys are the percentage labels. Four of the five values
 * the prompts list are not keys, so they stored an unrenderable probability and a
 * NULL stage_normalized: the exact shape CLAUDE.md calls "a shape the app cannot
 * produce", and the one P0.14 had already corrected in the seeder.
 *
 * Accepts a label unchanged (so a caller that already has one is not broken), maps
 * a canonical name, and falls back to Prospecting for anything unrecognised —
 * a wrong-but-valid stage the user can correct in the dropdown, rather than a null
 * the pipeline cannot render or filter.
 */
export function toProbabilityLabel(stage) {
  if (!stage) return STAGE_TO_PROBABILITY['Prospecting'];
  if (PROBABILITY_TO_STAGE[stage]) return stage;          // already a label
  return STAGE_TO_PROBABILITY[stage] || STAGE_TO_PROBABILITY['Prospecting'];
}

export function isActiveProbability(probability) {
  return ACTIVE_STAGES.includes(probability);
}
